// Shared artifact types + helpers for the Ros panel switcher. Extracted verbatim from the former
// RosApp so AppShell (state/handlers) and ChatWorkspace (render) can both import them.

// One published version of an artifact — enough to re-open it read-only via openArtifactVersion.
export interface PanelArtifactVersion {
  type: string
  version: number
  title: string
  /** Chat that produced this version. Uploaded artifacts may not have one. */
  sessionId: string | null
  // Absent for markdown/text artifacts: their body lives in the artifact-lifecycle engine, not the
  // R2-key-addressed catalog body other types use.
  bodyR2Key?: string
  publishedAt: string
  summary?: { sourceRevision?: number | null; [key: string]: unknown }
  // Artifacts-library metadata (added in CL-33184). Null on versions published before the columns
  // existed; retentionDays falls back to the policy default. publishedAt is this version's created
  // date, so expiry is derived per version: expiresAt = publishedAt + retentionDays.
  sizeBytes?: number | null
  source?: string | null
  retentionDays?: number | null
  // Provenance (debug mode only) — which model actually produced this version, stamped at publish
  // time. Null on versions published before these columns existed, or if resolution failed.
  modelSpec?: string | null
  modelLabel?: string | null
}

// A published artifact for the current session (one per type), with its full version history.
// Drives the panel's artifact-picker + version-picker switcher.
export interface SessionArtifact {
  id: string
  type: string
  title: string
  /** Chat that produced the latest version. Null when created directly from the Artifacts page. */
  sessionId: string | null
  latestVersion: number
  isProtected: boolean
  publication?: {
    version: number
    access: 'public' | 'password'
    status: string
    path?: string
  } | null
  versions: PanelArtifactVersion[]
  sourceChatTitle?: string
}

// Most recent publish time across an artifact's versions (ISO strings sort lexicographically).
export function latestPublishedAt(a: SessionArtifact): string {
  return a.versions.reduce((max, v) => (v.publishedAt > max ? v.publishedAt : max), '')
}

// Friendly label for the artifact picker — title plus a short type tag to disambiguate types.
export function artifactLabel(a: SessionArtifact): string {
  const tag = a.type === 'crosstabs' ? 'Crosstabs'
    : a.type === 'website' ? 'Website'
    : a.type === 'survey' ? 'Survey'
    : a.type === 'markdown' ? 'Markdown'
    : a.type === 'app-artifact' ? 'App Artifact'
    : a.type
  return `${a.title} · ${tag}`
}

// Map a published-artifact type to the session.panelType used by the draft-banner endpoints.
export function artifactTypeToPanelType(type: string): string | undefined {
  return type === 'crosstabs' ? 'crosstabs'
    : type === 'website' ? 'site_builder'
    : type === 'survey' ? 'survey_review'
    : type === 'markdown' ? 'markdown_artifact'
    : undefined
}
